import React, { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import {
  addSessionComment,
  deleteSessionComment,
  fetchSessionComments,
} from '../../services/training';

export default function CommentSection({ sessionId }) {
  const { user, profile, can } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const canModerate = can('moderate_training_comments');

  const loadComments = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await fetchSessionComments(sessionId);
      setComments(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      const created = await addSessionComment(sessionId, newComment);
      setComments((prev) => [created, ...prev]);
      setNewComment('');
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await deleteSessionComment(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      console.error(err);
    }
  };

  const avatarLetter = (profile?.full_name || 'T').charAt(0).toUpperCase();

  const formatTime = (iso) => {
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es });
    } catch {
      return '';
    }
  };

  return (
    <div className="mt-8">
      <h3 className="text-xl font-bold text-[var(--text-main)] mb-6 flex items-center gap-2">
        <SafeIcon name="MessageSquare" className="w-5 h-5 text-brand-cyan" />
        Discusión ({comments.length})
      </h3>

      <form onSubmit={handleSubmit} className="flex gap-4 mb-8">
        <div className="w-10 h-10 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex-shrink-0 flex items-center justify-center text-sm font-bold text-[var(--text-main)]">
          {avatarLetter}
        </div>
        <div className="flex-1 relative">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Añade tu comentario o pregunta..."
            className="w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 pr-12 text-sm text-[var(--text-main)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-all"
          />
          <button
            type="submit"
            disabled={!newComment.trim() || submitting}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-brand-green hover:text-brand-cyan disabled:text-slate-400 transition-colors"
          >
            <SafeIcon name="Send" className="w-4 h-4" />
          </button>
        </div>
      </form>

      {loading ? (
        <p className="text-sm text-[var(--text-secondary)]">Cargando comentarios…</p>
      ) : (
        <div className="space-y-6">
          {comments.map((comment) => {
            const canDelete = comment.userId === user?.id || canModerate;
            return (
              <div key={comment.id} className="flex gap-4 group">
                <div className="w-10 h-10 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex-shrink-0 flex items-center justify-center text-sm font-bold text-brand-cyan">
                  {comment.avatar}
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-semibold text-[var(--text-main)] text-sm">{comment.user}</span>
                    <span className="text-[10px] font-bold uppercase tracking-tighter text-[var(--text-secondary)]">
                      {formatTime(comment.createdAt)}
                    </span>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(comment.id)}
                        className="ml-auto text-[10px] font-bold uppercase tracking-widest text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Eliminar
                      </button>
                    )}
                  </div>
                  <p className="text-[var(--text-main)]/70 text-sm leading-relaxed">{comment.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
